import { useCallback, useMemo, useRef, useState } from 'react';

import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Link as MuiLink,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { type FieldPath, useController } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type {
  FileFragment,
  ListingOverviewPublishDocumentFieldFragment,
} from '@graphql/__generated__/graphql';
import { FileIcon } from '@modules/_shared/components/FileIcon';
import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import {
  type RaSelectOption,
  SelectField,
} from '@modules/_shared/components/form/RaSelect';
import type { TOtherMediaFormData } from '@modules/_shared/components/listing-overview-form/listingOverviewFormDefinition';
import {
  isMimetypeAllowedForPublication,
  isMimetypeVideo,
} from '@modules/_shared/components/PropertyFiles';
import { useTranslationWithComponents } from '@modules/_shared/hooks/useTranslationWithComponents';
import { useLocale } from '@shared/hooks/locale';

const FREE_TEXT_VALUE = '__free_text__';

type FileFilterType = 'documents' | 'videos';

type ListingOverviewPublishDocumentFieldProps<
  TName extends FieldPath<TOtherMediaFormData>,
> = {
  propertyFiles: ListingOverviewPublishDocumentFieldFragment['property']['property_files'];
  name: TName;
  fileType?: FileFilterType;
  allowFreeText?: boolean;
  valueProp?: 'url' | 'file_id';
};

export const LISTING_OVERVIEW_PUBLISH_DOCUMENT_FIELD_FRAGMENT =
  gql(/* GraphQL */ `
    fragment ListingOverviewPublishDocumentField on lots {
      id
      property {
        id
        property_files {
          ...PropertyFile
        }
      }
    }
  `);

type FileOption = RaSelectOption & { file?: FileFragment };

const EmptyListHelperText = ({ fileType }: { fileType: FileFilterType }) => {
  const t = useTranslationWithComponents();
  const [searchParams] = useSearchParams();

  return (
    <Typography variant="body2">
      {t('No files to publish, go to {{link}} to upload some', {
        link: (
          <MuiLink
            color="primary"
            underline="always"
            component={Link}
            to={{
              pathname: `media/${fileType}`,
              search: searchParams.toString(),
            }}
          >
            <strong>{t('the media tab')}</strong>
          </MuiLink>
        ),
      })}
    </Typography>
  );
};

export const ListingOverviewPublishDocumentField = <
  TName extends FieldPath<TOtherMediaFormData>,
>({
  propertyFiles,
  name,
  fileType = 'documents',
  allowFreeText = false,
  valueProp = 'file_id',
}: ListingOverviewPublishDocumentFieldProps<TName>) => {
  const { t } = useLocale();
  const config = useMemo(() => {
    return fileType === 'documents'
      ? {
          filterFn: (mimetype: string | null | undefined) =>
            isMimetypeAllowedForPublication(mimetype) &&
            !isMimetypeVideo(mimetype),
          labelKey: t('File to publish'),
          helperTextKey: t(
            'Only files that are allowed to be published on portals are shown (pdf/rtf/doc/ppt/docx/pptx/jpeg/png/gif/webp)',
          ),
        }
      : {
          filterFn: (mimetype: string | null | undefined) =>
            isMimetypeVideo(mimetype),
          labelKey: t('Video'),
          helperTextKey: t(
            'Only video files are shown (mp4/avi/mov/wmv/flv/mkv/3gp)',
          ),
        };
  }, [fileType, t]);

  const {
    field,
    fieldState: { error },
  } = useController<TOtherMediaFormData, TName>({
    name,
  });

  const fileOptions = useMemo(() => {
    return propertyFiles.reduce<FileOption[]>((acc, file) => {
      if (!config.filterFn(file.file.mimetype)) {
        return acc;
      }

      return [
        ...acc,
        {
          value: valueProp === 'file_id' ? file.file_id : file.file.url,
          label: file.file.name,
          file: file.file,
        },
      ];
    }, []);
  }, [propertyFiles, config, valueProp]);

  const [freeTextMode, setFreeTextMode] = useState(() => {
    if (!allowFreeText) {
      return false;
    }
    if (fileOptions.length === 0) {
      return true;
    }
    if (field.value == null || field.value === '') {
      return false;
    }
    return !fileOptions.some(opt => opt.value === field.value);
  });

  const didUserSwitch = useRef(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const options = useMemo(() => {
    const opts: FileOption[] = [...fileOptions];
    if (allowFreeText) {
      opts.push({
        value: FREE_TEXT_VALUE,
        label: t('Enter a URL...'),
      });
    }
    return opts;
  }, [fileOptions, allowFreeText, t]);

  const selectField = {
    ...field,
    value: freeTextMode ? FREE_TEXT_VALUE : field.value ?? '',
    onChange: (...event: any[]) => {
      const val = event[0]?.target?.value ?? event[0];
      if (val == null) {
        setFreeTextMode(false);
        field.onChange(null as any);
      } else if (val === FREE_TEXT_VALUE) {
        didUserSwitch.current = true;
        setFreeTextMode(true);
        field.onChange('' as any);
      } else {
        setFreeTextMode(false);
        field.onChange(event[0]);
      }
    },
  };

  const renderValue = useCallback(
    (value: string | null) => {
      if (value === FREE_TEXT_VALUE) {
        return field.value != null && field.value !== '' ? (
          <Typography noWrap sx={{ fontSize: 'inherit' }}>
            {field.value}
          </Typography>
        ) : (
          <Typography sx={{ color: 'text.disabled', fontSize: 'inherit' }}>
            {t('Enter a URL...')}
          </Typography>
        );
      }

      if (value == null || value === '') {
        return null;
      }

      const file = fileOptions.find(option => option.value === value)?.file;
      return file != null ? (
        <Typography noWrap sx={{ fontSize: 'inherit' }}>
          {file.name}
          {file.document_type != null ? ` (${file.document_type.label})` : ''}
        </Typography>
      ) : (
        value
      );
    },
    [fileOptions, t, field.value],
  );

  const renderOption = useCallback((option: RaSelectOption) => {
    const fileOpt = option as FileOption;

    return (
      <MenuItem key={fileOpt.value} value={fileOpt.value ?? ''}>
        {fileOpt.file != null ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', minWidth: 0, width: '100%' }}
          >
            <FileIcon
              mimetype={fileOpt.file.mimetype ?? ''}
              filename={fileOpt.file.name}
              size="medium"
            />
            <Typography noWrap sx={{ minWidth: 0, fontSize: 'inherit' }}>
              {fileOpt.label}
              {fileOpt.file.document_type != null
                ? ` (${fileOpt.file.document_type.label})`
                : ''}
            </Typography>
          </Stack>
        ) : (
          <Typography noWrap sx={{ fontSize: 'inherit' }}>
            {fileOpt.label}
          </Typography>
        )}
      </MenuItem>
    );
  }, []);

  const renderFileMenuItem = useCallback(
    (opt: FileOption) => (
      <MenuItem
        key={opt.value}
        onClick={() => {
          setMenuAnchor(null);
          setFreeTextMode(false);
          field.onChange(opt.value as any);
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', minWidth: 0, width: '100%' }}
        >
          {opt.file != null && (
            <FileIcon
              mimetype={opt.file.mimetype ?? ''}
              filename={opt.file.name}
              size="medium"
            />
          )}
          <Typography noWrap sx={{ minWidth: 0, fontSize: 'inherit' }}>
            {opt.label}
            {opt.file?.document_type != null
              ? ` (${opt.file.document_type.label})`
              : ''}
          </Typography>
        </Stack>
      </MenuItem>
    ),
    [field],
  );

  return (
    <FormControl fullWidth error={!!error}>
      <RaLabel label={config.labelKey} />
      {freeTextMode ? (
        <>
          <OutlinedInput
            ref={inputRef}
            value={field.value ?? ''}
            onChange={e => field.onChange(e.target.value as any)}
            autoComplete="off"
            autoFocus={didUserSwitch.current}
            size="small"
            fullWidth
            placeholder={t('Enter a URL...')}
            sx={{ background: 'var(--mui-palette-background-paper)' }}
            endAdornment={
              fileOptions.length > 0 && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={() => setMenuAnchor(inputRef.current)}
                  >
                    <ChevronDown size={20} />
                  </IconButton>
                </InputAdornment>
              )
            }
          />
          <FormHelperText>
            {error?.message ?? config.helperTextKey}
          </FormHelperText>
        </>
      ) : options.length > 0 ? (
        <>
          <SelectField
            field={selectField}
            options={options}
            renderValue={renderValue}
            renderOption={renderOption}
            matchMenuWidth
            clearable
          />
          <FormHelperText>
            {error?.message ?? config.helperTextKey}
          </FormHelperText>
        </>
      ) : (
        <>
          <EmptyListHelperText fileType={fileType} />
          <Typography variant="caption">{config.helperTextKey}</Typography>
        </>
      )}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{
          paper: { sx: { minWidth: menuAnchor?.clientWidth } },
        }}
      >
        {fileOptions.map(renderFileMenuItem)}
      </Menu>
    </FormControl>
  );
};
